import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Calendar as CalendarIcon, ChevronLeft, ChevronRight, X, Loader2, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { SITE_TZ, tzLabel, fmtTime, fmtDateTime, zonedParts, zonedToUtc, parseKey, addDaysKey } from '../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS = Array.from({ length: 13 }, (_, i) => i + 7); // 7am - 7pm
const STATUS_COLORS = {
  scheduled:    'bg-blue-500',
  completed:    'bg-emerald-500',
  no_show:      'bg-red-500',
  cancelled:    'bg-gray-400',
  rescheduled:  'bg-amber-500',
};

const WD = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
// Monday (YYYY-MM-DD) of the current week in site time.
function startOfWeek() {
  const p = zonedParts(new Date(), SITE_TZ);
  return addDaysKey(p.key, -WD[p.weekday]);
}
const keyToUtc = (key, h = 0, m = 0) => { const { year, month, day } = parseKey(key); return zonedToUtc(year, month, day, h, m, SITE_TZ); };
// "Central Time (CDT)" or "Central Time (CDT → CST)" when a DST change falls in the week.
const weekTzLabel = (key) => {
  const a = tzLabel(SITE_TZ, keyToUtc(key, 12));
  const b = tzLabel(SITE_TZ, keyToUtc(addDaysKey(key, 6), 12));
  return a === b ? a : `${a.split(' (')[0]} (${a.split('(')[1].replace(')', '')} → ${b.split('(')[1].replace(')', '')})`;
};
const keyLabel = (key, opts) => { const { year, month, day } = parseKey(key); return new Date(Date.UTC(year, month - 1, day, 12)).toLocaleDateString('en-US', { timeZone: 'UTC', ...opts }); };

export default function InterviewCalendar() {
  const [weekStart, setWeekStart] = useState(startOfWeek());
  const todayKey = zonedParts(new Date(), SITE_TZ).key;
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dragging, setDragging] = useState(null);

  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDaysKey(weekStart, i)), [weekStart]);

  const fetch = async () => {
    setLoading(true);
    try {
      const start = keyToUtc(weekStart);
      const end = keyToUtc(addDaysKey(weekStart, 7));
      const r = await axios.get(`${API}/admin/interviews`, {
        params: { start: start.toISOString(), end: end.toISOString() },
      });
      setInterviews(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, [weekStart]); // eslint-disable-line

  const interviewsForDayHour = (day, hour) => {
    return interviews.filter(iv => {
      if (!iv.starts_at) return false;
      const p = zonedParts(iv.starts_at, SITE_TZ);
      return p.key === day && p.hour === hour;
    });
  };

  const onDrop = async (day, hour) => {
    if (!dragging) return;
    const original = new Date(dragging.starts_at);
    const originalEnd = new Date(dragging.ends_at);
    const durationMs = originalEnd - original;
    const newStart = keyToUtc(day, hour, zonedParts(original, SITE_TZ).minute);
    const newEnd = new Date(newStart.getTime() + durationMs);
    try {
      await axios.put(`${API}/admin/interviews/${dragging.id}`, {
        starts_at: newStart.toISOString(),
        ends_at: newEnd.toISOString(),
      });
      toast.success('Interview rescheduled');
      setDragging(null);
      fetch();
    } catch (e) { toast.error('Reschedule failed'); }
  };

  const updateStatus = async (id, status) => {
    await axios.put(`${API}/admin/interviews/${id}`, { status });
    toast.success(`Marked as ${status.replace('_', ' ')}`);
    fetch();
    if (editing?.id === id) setEditing({ ...editing, status });
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this interview?')) return;
    await axios.delete(`${API}/admin/interviews/${id}`);
    toast.success('Interview deleted');
    setEditing(null);
    fetch();
  };

  const fmtRange = `${keyLabel(days[0], { month: 'short', day: 'numeric' })} – ${keyLabel(days[6], { month: 'short', day: 'numeric', year: 'numeric' })}`;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Link to="/admin/careers"><Button variant="ghost" size="sm"><ArrowLeft size={14} className="mr-1" /> Back to Careers</Button></Link>
      </div>

      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2"><CalendarIcon className="text-primary" /> Interview Calendar</h1>
        <p className="text-muted-foreground">Drag any interview to reschedule. Click to edit, change status, or delete.</p>
        <p className="text-sm font-medium text-primary mt-1" data-testid="calendar-tz-label">All times in {weekTzLabel(weekStart)}</p>
      </div>

      <Card className="p-3 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDaysKey(weekStart, -7))} data-testid="prev-week-btn"><ChevronLeft size={14} /></Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek())}>Today</Button>
          <Button variant="outline" size="sm" onClick={() => setWeekStart(addDaysKey(weekStart, 7))} data-testid="next-week-btn"><ChevronRight size={14} /></Button>
          <span className="font-semibold ml-2">{fmtRange}</span>
        </div>
        <div className="flex items-center gap-3 text-xs">
          {Object.entries(STATUS_COLORS).map(([s, cls]) => (
            <span key={s} className="inline-flex items-center gap-1">
              <span className={`w-2.5 h-2.5 rounded-full ${cls}`} /> {s.replace('_', ' ')}
            </span>
          ))}
        </div>
      </Card>

      <Card className="overflow-x-auto">
        {loading ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="animate-spin inline mr-2" /> Loading…</div>
        ) : (
          <div className="grid grid-cols-[60px_repeat(7,minmax(140px,1fr))] min-w-[1100px]">
            <div className="border-b border-r p-2 bg-muted/30" />
            {days.map((d, i) => (
              <div key={i} className="border-b border-r p-2 bg-muted/30 text-center">
                <div className="text-xs uppercase text-muted-foreground">{keyLabel(d, { weekday: 'short' })}</div>
                <div className={`text-lg font-semibold ${d === todayKey ? 'text-primary' : ''}`}>
                  {parseKey(d).day}
                </div>
              </div>
            ))}

            {HOURS.map(h => (
              <React.Fragment key={h}>
                <div className="border-b border-r p-1 text-xs text-muted-foreground text-right pr-2">
                  {h % 12 || 12}{h < 12 ? 'a' : 'p'}
                </div>
                {days.map((d, i) => {
                  const items = interviewsForDayHour(d, h);
                  return (
                    <div
                      key={i}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(d, h)}
                      className="border-b border-r p-1 min-h-[60px] hover:bg-muted/20 transition-colors space-y-1"
                      data-testid={`cal-cell-${d}-${h}`}
                    >
                      {items.map(iv => {
                        return (
                          <div
                            key={iv.id}
                            draggable
                            onDragStart={() => setDragging(iv)}
                            onClick={() => setEditing(iv)}
                            className={`${STATUS_COLORS[iv.status] || 'bg-blue-500'} text-white rounded text-[11px] px-1.5 py-1 cursor-move hover:opacity-90 leading-tight`}
                            data-testid={`interview-${iv.id}`}
                          >
                            <div className="font-semibold truncate">{iv.applicant_name}</div>
                            <div className="opacity-90 truncate">
                              {iv.booked_via === 'booking-link' ? 'Link' : `R${iv.round}`} · {fmtTime(iv.starts_at, SITE_TZ)} · {iv.interviewer_name}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
        )}
      </Card>

      {editing && <EditModal interview={editing} onClose={() => setEditing(null)} onStatusChange={updateStatus} onDelete={remove} onSave={async (patch) => {
        await axios.put(`${API}/admin/interviews/${editing.id}`, patch);
        toast.success('Updated');
        setEditing(null);
        fetch();
      }} />}
    </div>
  );
}

const ROUND_NUM = { '1st': 1, '2nd': 2, '3rd': 3, Final: 4 };
const ROUND_LABEL = { 1: '1st', 2: '2nd', 3: '3rd', 4: 'Final' };

function EditModal({ interview, onClose, onStatusChange, onDelete, onSave }) {
  const [notes, setNotes] = useState(interview.notes || '');
  const isLabelRound = typeof interview.round === 'string';
  const [round, setRound] = useState(isLabelRound ? (ROUND_NUM[interview.round] || 1) : (interview.round || 1));
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  const [location, setLocation] = useState(interview.location || 'Phone');

  return (
    <div className="fixed inset-0 bg-black/60 z-50 grid place-items-center p-4" onClick={onClose}>
      <Card className="max-w-lg w-full p-5 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2"><Briefcase size={16} /> Interview Details</h3>
          <Button variant="ghost" size="sm" onClick={onClose}><X size={14} /></Button>
        </div>
        <div className="text-sm space-y-1">
          <div><b>{interview.booked_via === 'booking-link' ? 'Booked by' : 'Applicant'}:</b> {interview.applicant_name} ({interview.applicant_email || '—'})</div>
          {interview.booker_phone && <div><b>Phone:</b> {interview.booker_phone}</div>}
          <div><b>Interviewer:</b> {interview.interviewer_name}</div>
          <div data-testid="interview-when"><b>When:</b> {fmtDateTime(interview.starts_at, SITE_TZ)}</div>
          {interview.booker_timezone && interview.booker_timezone !== SITE_TZ && <div className="text-xs text-muted-foreground">Booker's time: {fmtDateTime(interview.starts_at, interview.booker_timezone)}</div>}
          {interview.meeting_link && <div><b>Meet:</b> <a href={interview.meeting_link} target="_blank" rel="noreferrer" className="text-primary underline">{interview.meeting_link}</a></div>}
          <div className="text-xs text-muted-foreground">{interview.gcal_event_id ? 'Synced to Google Calendar' : 'Not on Google Calendar'}</div>
          {interview.last_email_status && <div className={`text-xs ${interview.last_email_status === 'failed' ? 'text-red-700' : 'text-muted-foreground'}`} data-testid="interview-email-status">Last booking email: {interview.last_email_status}{interview.last_email_failed_roles?.length ? ` (to ${interview.last_email_failed_roles.join(', ')})` : ''}</div>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Round</Label>
            <select value={round} onChange={e => setRound(parseInt(e.target.value))} className="h-10 w-full rounded-md border px-3 text-sm">
              {[1, 2, 3, 4].map(r => <option key={r} value={r}>{r === 1 ? '1st' : r === 2 ? '2nd' : r === 3 ? '3rd' : 'Final'}</option>)}
            </select>
          </div>
          <div>
            <Label>Location</Label>
            <select value={location} onChange={e => setLocation(e.target.value)} className="h-10 w-full rounded-md border px-3 text-sm">
              <option>Phone</option><option>Video</option><option>Google Meet</option><option>In-person</option>
              {!['Phone', 'Video', 'Google Meet', 'In-person'].includes(location) && <option>{location}</option>}
            </select>
          </div>
        </div>
        <div>
          <Label>Notes</Label>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm resize-y min-h-[70px]" />
        </div>
        <div>
          <Label>Status</Label>
          <div className="flex flex-wrap gap-1.5 mt-1">
            {Object.keys(STATUS_COLORS).map(s => (
              <Button key={s} size="sm" variant={interview.status === s ? 'default' : 'outline'} onClick={() => onStatusChange(interview.id, s)}>
                {s.replace('_', ' ')}
              </Button>
            ))}
          </div>
        </div>
        <div className="flex justify-between pt-3 border-t">
          <Button variant="ghost" onClick={() => onDelete(interview.id)} className="text-red-500">Delete</Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => onSave({ notes, round: isLabelRound ? ROUND_LABEL[round] : round, location })}>Save</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
